import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import '../styles/MainLayout.css';
import SeatMap from './SeatMap';
import GuestList from './GuestList';
import LanguageSwitcher from './LanguageSwitcher';
import type { RoomData, SeatToGuestMap, Guest, GroupContext } from '../types';
import { DEFAULT_MAX_ADDITIONAL_GUESTS, MAX_GUESTS, MIN_GUESTS } from '../config';
import { apiFetch } from '../utils/apiFetch';

interface MainLayoutProps {
  roomData: RoomData | null;
  seatToGuestMap: SeatToGuestMap;
  setSeatToGuestMap: React.Dispatch<React.SetStateAction<SeatToGuestMap>>;
  guestCodes: Guest[];
  setGuestCodes: React.Dispatch<React.SetStateAction<Guest[]>>;
  logo: string;
  footerLogo: string;
  appName: string;
  /** Live WebSocket connection state (true while STOMP subscription is open). */
  wsConnected?: boolean;
  children?: React.ReactNode; // Allow children for info page
  onAssignmentsConfirmed?: (table: string | null) => void;
  /** True if this session is a coordinator re-login for an existing group */
  extensionMode?: boolean;
  /** Loaded reservation group context (members, status, limits, current assignments) */
  groupContext?: GroupContext | null;
  /** Refetches the group context from the backend */
  refreshGroupContext?: () => Promise<GroupContext | null> | void;
}

const MainLayout: React.FC<MainLayoutProps> = ({
  roomData,
  seatToGuestMap,
  setSeatToGuestMap,
  guestCodes,
  setGuestCodes,
  logo,
  footerLogo,
  wsConnected = true,
  children,
  onAssignmentsConfirmed,
  extensionMode = false,
  groupContext = null,
  refreshGroupContext,
}) => {
  const { t, i18n } = useTranslation();
  // Count of locked (pre-confirmed) members; used for min/max calculations.
  const lockedCount = extensionMode
    ? (groupContext?.members ?? []).filter(m => m.memberType === 'LOCKED_INITIAL' && m.active).length
    : 0;
  const minGuestCodes = extensionMode ? lockedCount : MIN_GUESTS;
  const [showInfo, setShowInfo] = useState(() => sessionStorage.getItem('infoPageShown') !== 'true');
  // Set when the backend reports that the existing table is too small to
  // accommodate the extended group; the user must then pick a new table.
  const [tableChangeRequired, setTableChangeRequired] = useState(false);
  // Set when no table in the room can seat the whole group.
  const [noTablesAvailable, setNoTablesAvailable] = useState(false);
  // Prevents concurrent auto-seat API calls in extension mode.
  const autoSeatInFlight = useRef(false);

  // Mirror `guestCodes` in a ref so that async callbacks (e.g.
  // `handleTableClick`) always see the latest list without stale closures.
  // This replaces the former `(SeatMap as any).getLatestGuestCodes()`
  // static-method bridge.
  const guestCodesRef = useRef<Guest[]>(guestCodes);
  useEffect(() => {
    guestCodesRef.current = guestCodes;
  }, [guestCodes]);

  const handleGuestCodesUpdate = (guestObjects: Guest[]) => {
    // Sync seatGuid for each guest from seatToGuestMap
    const synced = guestObjects.map(g => {
      const found = Object.entries(seatToGuestMap).find(([, v]) => v.guestGuid === g.guestGuid);
      return found ? { ...g, seatGuid: found[0] } : { ...g, seatGuid: '' };
    });
    setGuestCodes(synced);
    sessionStorage.setItem('guestCodes', JSON.stringify(synced));
  };

  const handleAssignmentsConfirmed = (table: string | null) => {
    if (onAssignmentsConfirmed) {
      onAssignmentsConfirmed(table);
    }
  }

  // Helper to get API base path dynamically
  function getApiBase(): string {
    return `${window.location.protocol}//${window.location.host}/seatmap_api/`;
  }

  // Helper to sync guestCodes seatGuid from seatToGuestMap
  function syncGuestCodesWithSeatMap(guestCodes: Guest[], seatToGuestMap: SeatToGuestMap): Guest[] {
    return guestCodes.map(g => {
      const found = Object.entries(seatToGuestMap).find(([, v]) => v.guestGuid === g.guestGuid);
      return found ? { ...g, seatGuid: found[0] } : { ...g, seatGuid: '' };
    });
  }

  const handleSeatClick = (seat: any) => {
    if (guestCodes.length >= minGuestCodes) {
      const guest = guestCodes.find(g => g.guestGuid && g.valid === true && !g.seatGuid);
      if (guest) {
        setSeatToGuestMap((prevMap) => {
          const updatedMap = { ...prevMap, [seat.guid]: guest.guestGuid };
          sessionStorage.setItem('seatToGuestMap', JSON.stringify(updatedMap));
          // Sync guestCodes with new seat assignments
          const updatedGuestCodes = syncGuestCodesWithSeatMap(guestCodes, updatedMap);
          setGuestCodes(updatedGuestCodes);
          sessionStorage.setItem('guestCodes', JSON.stringify(updatedGuestCodes));
          return updatedMap;
        });
      } else {
        console.log('No valid, unassigned guest available.');
      }
    } else {
      console.log('Not enough guest codes entered to assign seats.');
    }
  };

  // Build a seat-GUID ? table-GUID lookup from roomData.
  // `seatToGuestMap[seatGuid].tableID` is a legacy numeric label (e.g. "14")
  // while `roomData.groups[].children[].guid` is a proper GUID; the two
  // formats never match, so we derive table identity from this map instead.
  const seatGuidToTableGuid = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    if (!roomData) return map;
    for (const g of (roomData as any).groups || []) {
      for (const tbl of (g.children || []).filter((c: any) => c.type === 'table')) {
        const tblGuid: string = tbl.guid || '';
        if (!tblGuid) continue;
        for (const seat of (tbl.seats || [])) {
          if (seat.guid) map[seat.guid] = tblGuid;
        }
      }
    }
    return map;
  }, [roomData]);

  // Compute the current target table GUID for extension auto-seat.
  // Prefer live seatToGuestMap (authoritative and updated by external admin moves),
  // then fall back to reservation currentAssignments if no live mapping is found.
  const groupTableId = useMemo(() => {
    if (!groupContext) return null;

    const activeGuestIds = new Set(
      (groupContext.members || [])
        .filter(m => m.active)
        .map(m => m.guestId)
        .filter((id): id is string => !!id)
    );
    if (activeGuestIds.size > 0) {
      // 1) Coordinator seat wins if currently seated.
      const coordinatorGuestId = groupContext.group.coordinatorGuestId;
      if (coordinatorGuestId) {
        for (const [seatGuid, assignment] of Object.entries(seatToGuestMap)) {
          if (assignment?.guestGuid === coordinatorGuestId) {
            const tableGuid = seatGuidToTableGuid[seatGuid];
            if (tableGuid) return tableGuid;
          }
        }
      }

      // 2) Otherwise use the most frequent live table among active group members.
      const liveTableCounts: Record<string, number> = {};
      for (const [seatGuid, assignment] of Object.entries(seatToGuestMap)) {
        const guestGuid = assignment?.guestGuid;
        if (!guestGuid || !activeGuestIds.has(guestGuid)) continue;
        const tableGuid = seatGuidToTableGuid[seatGuid];
        if (!tableGuid) continue;
        liveTableCounts[tableGuid] = (liveTableCounts[tableGuid] || 0) + 1;
      }
      const bestLiveTable = Object.entries(liveTableCounts)
        .sort((a, b) => b[1] - a[1])[0]?.[0];
      if (bestLiveTable) return bestLiveTable;
    }

    // 3) Fallback: extension history current assignments.
    const ca = groupContext.currentAssignments || [];
    if (ca.length === 0) return null;
    const seatId = ca[0].seatId;
    return seatGuidToTableGuid[seatId] || null;
  }, [groupContext, seatToGuestMap, seatGuidToTableGuid]);

  // Freeze the original persisted group table at extension-session start.
  // This avoids comparing against a live/provisional overlay table after
  // prepare-table-move, which could incorrectly produce tableChanged=false.
  const initialGroupTableGuidRef = useRef<string | null>(null);
  const initialGroupTableDisplayRef = useRef<string | null>(null);
  useEffect(() => {
    if (!extensionMode || !groupContext) {
      initialGroupTableGuidRef.current = null;
      initialGroupTableDisplayRef.current = null;
      return;
    }
    if (!initialGroupTableDisplayRef.current) {
      initialGroupTableDisplayRef.current = groupContext.group.currentTableId ?? null;
    }
    if (!initialGroupTableGuidRef.current && groupTableId) {
      initialGroupTableGuidRef.current = groupTableId;
    }
  }, [extensionMode, groupContext, groupTableId]);

  const resolveTableDisplayIdByGuid = useCallback((tableGuid: string | null): string | null => {
    if (!tableGuid) return null;
    if (!roomData) return tableGuid;
    for (const g of (roomData as any).groups || []) {
      for (const tbl of [...(g.children || []).filter((c: any) => c.type === 'table'), ...(g.tables || [])]) {
        if (tbl.guid === tableGuid || tbl.id === tableGuid || tbl.tableID === tableGuid) {
          return tbl.tableId || tbl.label || tbl.name || tbl.table || tableGuid;
        }
      }
    }
    return tableGuid;
  }, [roomData]);

  // Compute the seat capacity (number of seats) of a given tableId by walking
  // the groups/tables in roomData.
  const tableCapacity = useCallback((tableId: string | null): number => {
    if (!tableId || !roomData) return 0;
    for (const g of (roomData as any).groups || []) {
      for (const tbl of [...(g.children || []).filter((c: any) => c.type === 'table'), ...(g.tables || [])]) {
        if (tbl.guid === tableId || tbl.id === tableId || tbl.tableID === tableId) {
          return (tbl.seats || []).length;
        }
      }
    }
    return 0;
  }, [roomData]);

  // Find the full table object (with seats) by tableId.
  const findTableById = useCallback((tableId: string | null) => {
    if (!tableId || !roomData) return null;
    for (const g of (roomData as any).groups || []) {
      for (const tbl of [...(g.children || []).filter((c: any) => c.type === 'table'), ...(g.tables || [])]) {
        if (tbl.guid === tableId || tbl.id === tableId || tbl.tableID === tableId) {
          return tbl;
        }
      }
    }
    return null;
  }, [roomData]);

  // Effective max additional guests: group policy capped by physical table capacity.
  // Prevents showing "N more can be added" when no table can fit the full group.
  const effectiveMaxAdditional = extensionMode
    ? Math.min(
        groupContext?.group?.maxAdditionalGuests ?? DEFAULT_MAX_ADDITIONAL_GUESTS,
        Math.max(0, tableCapacity(groupTableId) - lockedCount)
      )
    : DEFAULT_MAX_ADDITIONAL_GUESTS;
  const maxGuestCodes = extensionMode ? lockedCount + effectiveMaxAdditional : MAX_GUESTS;

  // Check whether any table OTHER than the current group table can seat all guests.
  const checkAnyTableFits = useCallback((totalGroupSize: number): boolean => {
    if (!roomData) return false;
    for (const g of (roomData as any).groups || []) {
      for (const tbl of [...(g.children || []).filter((c: any) => c.type === 'table'), ...(g.tables || [])]) {
        const tblId = tbl.guid || tbl.id;
        if (tblId === groupTableId) continue;
        const totalSeats = (tbl.seats || []).length;
        const occupiedByOthers = Object.entries(seatToGuestMap).filter(
          ([sg, a]: [string, any]) => seatGuidToTableGuid[sg] === tblId && a?.guestGuid
        ).length;
        if (totalSeats - occupiedByOthers >= totalGroupSize) return true;
      }
    }
    return false;
  }, [roomData, groupTableId, seatToGuestMap, seatGuidToTableGuid]);

  // Auto-seat new valid guests at the group table when there is still room.
  // Runs whenever guestCodes or seatToGuestMap changes in extension mode.
  useEffect(() => {
    if (!extensionMode || !groupTableId || autoSeatInFlight.current) return;

    // Guests that need a seat: newly entered (non-locked) guests AND any
    // locked member who had no persisted seat assignment at login time
    // (e.g. their original seat was cleared by an admin). The latter are
    // identified by absence from groupContext.currentAssignments.
    const persistedGuestIds = new Set(
      (groupContext?.currentAssignments ?? []).map((a: any) => a.guestId as string)
    );
    const newUnseated = guestCodes.filter(g =>
      !!g.guestGuid?.trim() &&
      g.valid === true &&
      !Object.values(seatToGuestMap).some((a: any) => a?.guestGuid === g.guestGuid) &&
      (!g.locked || !persistedGuestIds.has(g.guestGuid ?? ''))
    );
    if (newUnseated.length === 0) return;

    const totalAtGroup = tableCapacity(groupTableId);
    const occupiedAtGroup = Object.entries(seatToGuestMap).filter(
      ([sg, a]: [string, any]) => seatGuidToTableGuid[sg] === groupTableId && a?.guestGuid
    ).length;
    const freeAtGroup = totalAtGroup - occupiedAtGroup;

    if (newUnseated.length > freeAtGroup) {
      // Current table is full. Check if any other table fits all.
      const totalGroupSize = guestCodes.filter(g => !!g.guestGuid?.trim() && g.valid !== false).length;
      const anyFits = checkAnyTableFits(totalGroupSize);
      setNoTablesAvailable(!anyFits);
      if (anyFits) setTableChangeRequired(true);
      return;
    }

    // Reset overflow states when there is room again.
    setTableChangeRequired(false);
    setNoTablesAvailable(false);

    const tableObj = findTableById(groupTableId);
    if (!tableObj) return;

    const unassignedSeats = (tableObj.seats || []).filter(
      (seat: any) => seatToGuestMap[seat.guid]?.guestGuid == null
    );
    if (unassignedSeats.length === 0) return;

    const assignments: Record<string, string> = {};
    // Re-send any non-locked guests already seated from prior auto-seat rounds.
    // The backend replaces the entire session map on each POST, so omitting
    // previously-seated guests would clear their tentative seat assignment and
    // cause them to appear as unseated again on the next effect run (loop).
    for (const g of guestCodes) {
      if (g.guestGuid && g.seatGuid && !g.locked) {
        assignments[g.seatGuid] = g.guestGuid;
      }
    }
    const toAssign = newUnseated.slice(0, unassignedSeats.length);
    for (let i = 0; i < toAssign.length; i++) {
      assignments[(unassignedSeats[i] as any).guid] = toAssign[i].guestGuid!;
    }
    if (Object.keys(assignments).length === 0) return;

    autoSeatInFlight.current = true;
    const sessionId = window.sessionStorage.getItem('sessionId');
    const apiBase = getApiBase();
    apiFetch(`${apiBase}seatmap/assignments/${sessionId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(assignments),
    })
      .then(res => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((updatedMap: SeatToGuestMap) => {
        setSeatToGuestMap(updatedMap);
        sessionStorage.setItem('seatToGuestMap', JSON.stringify(updatedMap));
        // Use guestCodesRef.current (not the stale closure value) so that codes
        // entered by the user while this POST was in flight are not overwritten.
        const updatedCodes = syncGuestCodesWithSeatMap(guestCodesRef.current, updatedMap);
        setGuestCodes(updatedCodes);
        sessionStorage.setItem('guestCodes', JSON.stringify(updatedCodes));
      })
      .catch(() => { /* silent � user can click table manually as fallback */ })
      .finally(() => { autoSeatInFlight.current = false; });
  }, [guestCodes, seatToGuestMap, extensionMode, groupTableId, tableCapacity, findTableById, checkAnyTableFits, seatGuidToTableGuid]);

  // Build the assignments payload sent to the backend extension endpoints.
  const buildExtensionAssignments = (codes: Guest[]): Array<{guestId: string; seatId: string; tableId: string}> => {
    const out: Array<{guestId: string; seatId: string; tableId: string}> = [];
    for (const g of codes) {
      if (!g.guestGuid || !g.seatGuid) continue;
      const seatEntry = seatToGuestMap[g.seatGuid];
      const tableId = seatEntry?.tableID;
      if (!tableId) continue;
      out.push({ guestId: g.guestGuid, seatId: g.seatGuid, tableId });
    }
    return out;
  };

  // Confirm assignments handler
  const handleConfirmAssignments = useCallback(async () => {
    // -------- Extension mode --------
    if (extensionMode && groupContext) {
      const apiBase = getApiBase();
      const groupId = groupContext.group.id;
      const coordinatorGuestId = groupContext.group.coordinatorGuestId;
      // Newly added guests (non-locked, validated) � these need to be persisted.
      const addedGuests = guestCodes.filter(g => !g.locked && g.guestGuid && g.guestGuid.trim() !== '');
      try {
        if (addedGuests.length > 0) {
          const addRes = await apiFetch(`${apiBase}groups/${groupId}/members:add`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              guestIds: addedGuests.map(g => g.guestGuid),
            }),
          });
          if (!addRes.ok) {
            alert(t('extensionAddFailed') || 'Failed to add additional guests.');
            return;
          }
        }

        // Determine if the user has moved the group to a new table since
        // the last persisted assignments. IMPORTANT: compare against the
        // frozen baseline table from extension-session start, not the live
        // overlay table which may already reflect a provisional move.
        const persistedGroupTableGuid = initialGroupTableGuidRef.current ?? groupTableId;
        const persistedGroupTableDisplay = initialGroupTableDisplayRef.current
          ?? groupContext.group.currentTableId
          ?? resolveTableDisplayIdByGuid(persistedGroupTableGuid);

        const activeParticipants = guestCodes.filter(g =>
          !!g.guestGuid?.trim() && (g.locked || g.valid === true)
        );
        const participantTableGuids = new Set<string>();
        for (const g of activeParticipants) {
          if (!g.seatGuid) continue;
          const tg = seatGuidToTableGuid[g.seatGuid];
          if (tg) participantTableGuids.add(tg);
        }

        if (participantTableGuids.size > 1) {
          alert(t('extensionConfirmFailed')
            || 'Die Gruppe ist auf mehrere Tische verteilt. Bitte den Tischwechsel erneut ausführen.');
          return;
        }

        const singleParticipantTableGuid = participantTableGuids.size === 1
          ? Array.from(participantTableGuids)[0]
          : null;
        const coordinator = guestCodes.find(g => g.guestGuid === coordinatorGuestId);
        const currentSeatId = coordinator?.seatGuid;
        const coordinatorTableGuid = currentSeatId ? (seatGuidToTableGuid[currentSeatId] ?? null) : null;
        const currentTableId = singleParticipantTableGuid ?? coordinatorTableGuid;
        const currentTableDisplay = resolveTableDisplayIdByGuid(currentTableId);

        const tableChangedByGuid = !!currentTableId
          && !!persistedGroupTableGuid
          && currentTableId !== persistedGroupTableGuid;
        const tableChangedByDisplay = !!currentTableDisplay
          && !!persistedGroupTableDisplay
          && currentTableDisplay !== persistedGroupTableDisplay;
        const tableChanged = tableChangedByGuid || (!persistedGroupTableGuid && tableChangedByDisplay);

        // Validate capacity against target table. Backend computes effective
        // capacity from authoritative DB state using the display table id.
        const targetTableGuid = tableChanged ? currentTableId : (persistedGroupTableGuid ?? groupTableId);
        const targetTableDisplay = tableChanged
          ? currentTableDisplay
          : (persistedGroupTableDisplay ?? resolveTableDisplayIdByGuid(targetTableGuid));
        const valRes = await apiFetch(`${apiBase}groups/${groupId}:validate-capacity`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            currentTableId: targetTableDisplay,
          }),
        });
        if (!valRes.ok) {
          alert(t('extensionValidateFailed') || 'Capacity validation failed.');
          return;
        }
        const valData = await valRes.json();
        if (valData.tableChangeRequired) {
          setTableChangeRequired(true);
          // Refresh group context so newly added members appear as locked rows
          // (they remain editable until confirm-extension persists the seats).
          if (refreshGroupContext) await refreshGroupContext();
          alert(t('tableChangeRequired') || 'A table change is required to fit all guests.');
          return;
        }

        // Capacity OK -> confirm extension on (possibly new) table.
        // Include: all members when table changed, plus any locked member who
        // was not in currentAssignments at login (originally unseated � now
        // re-seated at the group table and must be persisted).
        const persistedGuestIds = new Set(
          (groupContext.currentAssignments ?? []).map((a: any) => a.guestId as string)
        );
        const fullMoveMembers = guestCodes.filter(g =>
          !!g.guestGuid?.trim() && (g.locked || g.valid === true)
        );
        if (tableChanged && fullMoveMembers.some(g => !g.seatGuid)) {
          alert(t('extensionConfirmFailed') || 'Nicht alle Gruppenmitglieder haben einen Sitzplatz.');
          return;
        }
        const assignmentSource = tableChanged
          ? fullMoveMembers
          : guestCodes.filter(g => !g.locked || !persistedGuestIds.has(g.guestGuid ?? ''));
        const confirmRes = await apiFetch(`${apiBase}groups/${groupId}:confirm-extension`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tableChanged,
            newTableId: tableChanged ? currentTableDisplay : undefined,
            assignments: buildExtensionAssignments(assignmentSource),
            sessionId: window.sessionStorage.getItem('sessionId'),
          }),
        });
        if (!confirmRes.ok) {
          alert(t('extensionConfirmFailed') || 'Failed to confirm extension.');
          return;
        }
        if (refreshGroupContext) await refreshGroupContext();
        // Trigger the same post-confirmation UI as the initial flow.
        if (onAssignmentsConfirmed) {
          const finalTableId = tableChanged
            ? currentTableId
            : (persistedGroupTableGuid ?? groupTableId);
          const finalTableObj: any = findTableById(finalTableId);
          // Use the same label-resolution order as GroupRenderer:
          // tableId (backend numeric label) → label → name → table → fallback to GUID
          const finalDisplayTable =
            finalTableObj?.tableId || finalTableObj?.label || finalTableObj?.name || finalTableObj?.table || finalTableId;
          onAssignmentsConfirmed(finalDisplayTable ?? null);
        }
      } catch (e) {
        console.error('extension confirm failed', e);
        alert(t('extensionConfirmFailed') || 'Failed to confirm extension.');
      }
      return;
    }

    // -------- Initial confirmation mode (existing behavior + dual-write) --------
    // Only use guests with a non-empty guestCode, non-empty guestGuid, and not already assigned
    const validGuests = guestCodes.filter(g =>
      g.guestCode && g.guestCode.trim() !== '' &&
      g.guestGuid && g.guestGuid.trim() !== ''
    );
    if (validGuests.length >= minGuestCodes) {
      // Find all seats assigned to these guests
      const assignments: Record<string, string> = {};
      for (const guest of validGuests) {
        if (guest.seatGuid) {
          assignments[guest.seatGuid] = guest.guestGuid;
        }
      }
      if (Object.keys(assignments).length === 0) return;
      try {
        const sessionId = window.sessionStorage.getItem('sessionId');
        const apiBase = getApiBase();
        const response = await apiFetch(`${apiBase}seatmap/assignments/${sessionId}?confirmed=true`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(assignments),
        });
        if (response.status === 200) {
          const updatedMap = await response.json();
          setSeatToGuestMap(updatedMap);
          sessionStorage.setItem('seatToGuestMap', JSON.stringify(updatedMap));
          // Sync guestCodes with new seat assignments
          const updatedGuestCodes = syncGuestCodesWithSeatMap(guestCodes, updatedMap);
          setGuestCodes(updatedGuestCodes);
          sessionStorage.setItem('guestCodes', JSON.stringify(updatedGuestCodes));

          // Dual-write: create the reservation group on the extension backend.
          // Best-effort � the seatmap confirmation is authoritative; failures here
          // only affect the post-confirmation extension feature.
          try {
            const eventId = (roomData as any)?.eventId;
            const coordinator = validGuests[0];
            // Determine the table from the first assigned seat.
            const firstSeatId = coordinator?.seatGuid;
            const tableId = firstSeatId ? updatedMap[firstSeatId]?.tableID : undefined;
            if (eventId && coordinator?.guestGuid && tableId) {
              const memberGuestIds = validGuests
                .map(g => g.guestGuid)
                .filter((id): id is string => !!id);
              const groupAssignments = validGuests
                .filter(g => g.seatGuid)
                .map(g => ({ guestId: g.guestGuid, seatId: g.seatGuid!, tableId }));
              const _token = sessionStorage.getItem('seatmapToken');
              await fetch(`${apiBase}groups`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  ...(_token ? { 'Authorization': `Bearer ${_token}` } : {}),
                },
                body: JSON.stringify({
                  eventId,
                  coordinatorGuestId: coordinator.guestGuid,
                  tableId,
                  memberGuestIds,
                  assignments: groupAssignments,
                }),
              });
            }
          } catch (groupErr) {
            // Non-fatal: log and continue.
            console.warn('reservation group dual-write failed', groupErr);
          }

          // Only show final confirmation after successful backend confirmation.
          if (onAssignmentsConfirmed) {
            const firstSeatIdForUi = validGuests[0]?.seatGuid;
            const rawTableId = firstSeatIdForUi ? updatedMap[firstSeatIdForUi]?.tableID : null;
            const tableObj: any = findTableById(rawTableId ?? null);
            const displayTable = tableObj?.tableID || tableObj?.id || tableObj?.guid || rawTableId || null;
            onAssignmentsConfirmed(displayTable);
          }
        } else if (response.status === 409) {
          alert('Assignment confirmation failed: Some seats have already been taken.');
        } else {
          alert('Assignment confirmation failed: Unexpected server response.');
        }
      } catch (e) {
        alert('Assignment confirmation failed: Network or server error.');
      }
    } else {
      console.log('Not enough valid guests entered to confirm assignments.');
    }
  }, [guestCodes, setSeatToGuestMap, minGuestCodes, extensionMode, groupContext, groupTableId, refreshGroupContext, onAssignmentsConfirmed, roomData, t, seatGuidToTableGuid, findTableById, resolveTableDisplayIdByGuid]);

  // Table click handler using always up-to-date guest codes
  const handleTableClick = useCallback(
    async (table: any) => {
      const clickedTableId = table.guid || table.id || table.tableID;
      const baselineGroupTableId = initialGroupTableGuidRef.current ?? groupTableId;
      // Whether the user is moving the whole group to a different table.
      let isGroupTableMove = false;

      // In extension mode, clicks on tables OTHER than the group's current
      // table are only allowed when the current table cannot seat all new guests.
      if (extensionMode) {
        if (baselineGroupTableId && clickedTableId !== baselineGroupTableId) {
          // Count truly free seats at the current group table (occupied by nobody).
          const totalAtGroup = tableCapacity(baselineGroupTableId);
          const occupiedAtGroup = Object.entries(seatToGuestMap).filter(
            ([sg, a]) => seatGuidToTableGuid[sg] === baselineGroupTableId && a?.guestGuid
          ).length;
          const freeAtGroup = totalAtGroup - occupiedAtGroup;

          // New guests = non-locked entries with a valid guestGuid not yet
          // seated at the current group table.
          const latestCodes = guestCodesRef.current;
          const newGuests = latestCodes.filter(g =>
            !g.locked &&
            g.guestGuid && g.guestGuid.trim() !== '' &&
            !Object.entries(seatToGuestMap).some(
              ([sg, a]) => a?.guestGuid === g.guestGuid && seatGuidToTableGuid[sg] === baselineGroupTableId
            )
          );

          if (newGuests.length <= freeAtGroup) {
            // Current table still has room � block the change.
            alert(t('tableChangeNotAllowed')
              || 'Sie m\u00fcssen den bestehenden Tisch beibehalten, da gen\u00fcgend Pl\u00e4tze vorhanden sind.');
            return;
          }
          // Current table is full ? move the whole group to the new table.
          isGroupTableMove = true;
          setTableChangeRequired(true);
        }
      }

      const latestGuestCodes = guestCodesRef.current;
      // Human-readable table label (same resolution order as GroupRenderer).
      const clickedTableLabel = (table as any).tableId || (table as any).label || (table as any).name || (table as any).table || (table as any).id || (table as any).guid || '';
      // Non-group move behavior: only newly entered (non-locked) guests are
      // provisionally assigned on table click.
      const validGuests = latestGuestCodes.filter(g =>
        !g.locked &&
        (isGroupTableMove ? true : (g.guestCode && g.guestCode.trim() !== '')) &&
        g.guestGuid && g.guestGuid.trim() !== ''
      );

      if (isGroupTableMove) {
        const sessionId = window.sessionStorage.getItem('sessionId');
        const apiBase = getApiBase();
        const groupId = groupContext?.group?.id;
        if (!sessionId || !groupId) {
          alert('Assignment failed: Missing session or group context.');
          return;
        }

        // Include all current group participants: locked initial members and
        // validated newly-added guests.
        const participants = latestGuestCodes.filter(g =>
          !!g.guestGuid?.trim() &&
          (g.locked || g.valid === true)
        );
        const participantIds = new Set(
          participants
            .map(g => g.guestGuid)
            .filter((id): id is string => !!id)
        );

        // Preserve any participant already seated on the clicked table, then
        // assign remaining participants to still-free seats.
        const seatByGuest = new Map<string, string>();
        const usedSeats = new Set<string>();
        for (const seat of table.seats as any[]) {
          const occupiedGuestId = seatToGuestMap[seat.guid]?.guestGuid;
          if (occupiedGuestId && participantIds.has(occupiedGuestId)) {
            seatByGuest.set(occupiedGuestId, seat.guid);
            usedSeats.add(seat.guid);
          }
        }

        const freeSeats = (table.seats as any[]).filter((seat: any) => {
          if (usedSeats.has(seat.guid)) return false;
          const occupiedGuestId = seatToGuestMap[seat.guid]?.guestGuid;
          return !occupiedGuestId;
        });

        const remainingGuests = participants
          .map(g => g.guestGuid!)
          .filter(guestId => !seatByGuest.has(guestId));

        if (remainingGuests.length > freeSeats.length) {
          alert(t('tableChangeRequired') || 'A table change is required to fit all guests.');
          return;
        }

        for (let i = 0; i < remainingGuests.length; i++) {
          seatByGuest.set(remainingGuests[i], freeSeats[i].guid);
        }

        const moveAssignments = Array.from(seatByGuest.entries()).map(([guestId, seatId]) => ({
          guestId,
          seatId,
          tableId: String(clickedTableLabel || clickedTableId || ''),
        }));

        if (moveAssignments.length === 0) return;

        try {
          const response = await apiFetch(`${apiBase}groups/${groupId}:prepare-table-move`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sessionId,
              assignments: moveAssignments,
            }),
          });
          if (!response.ok) {
            alert('Assignment failed: Unable to prepare table move.');
            return;
          }
          const updatedMap: SeatToGuestMap = await response.json();
          setSeatToGuestMap(updatedMap);
          sessionStorage.setItem('seatToGuestMap', JSON.stringify(updatedMap));
          const updatedGuestCodes = syncGuestCodesWithSeatMap(latestGuestCodes, updatedMap);
          setGuestCodes(updatedGuestCodes);
          sessionStorage.setItem('guestCodes', JSON.stringify(updatedGuestCodes));
          return;
        } catch (e) {
          alert('Assignment failed: Network or server error.');
          return;
        }
      }

      if (extensionMode ? validGuests.length > 0 : validGuests.length >= minGuestCodes) {
        // Find seats on this table that are not assigned to a guest already seated at this table
        const guestGuidsSeatedAtTable = table.seats
          .map((seat: any) => seatToGuestMap[seat.guid]?.guestGuid)
          .filter((guid: string | undefined) => guid !== undefined);
        // Only assign guests who are not already seated at this table
        const guestsToAssign = validGuests.filter(g => !guestGuidsSeatedAtTable.includes(g.guestGuid));
        const unassignedSeats = table.seats.filter((seat: any) => seatToGuestMap[seat.guid]?.guestGuid == null);
        const assignments: Record<string, string> = {};
        for (let i = 0; i < Math.min(guestsToAssign.length, unassignedSeats.length); i++) {
          assignments[unassignedSeats[i].guid] = guestsToAssign[i].guestGuid;
          guestsToAssign[i].seatGuid = unassignedSeats[i].guid;
        }
        if (Object.keys(assignments).length === 0) return;
        try {
          const sessionId = window.sessionStorage.getItem('sessionId');
          const apiBase = getApiBase();
          const response = await apiFetch(`${apiBase}seatmap/assignments/${sessionId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(assignments),
          });
          if (response.status === 200) {
            const updatedMap: SeatToGuestMap = await response.json();
            setSeatToGuestMap(updatedMap);
            sessionStorage.setItem('seatToGuestMap', JSON.stringify(updatedMap));
            const updatedGuestCodes = syncGuestCodesWithSeatMap(latestGuestCodes, updatedMap);
            setGuestCodes(updatedGuestCodes);
            sessionStorage.setItem('guestCodes', JSON.stringify(updatedGuestCodes));
          } else if (response.status === 409) {
            alert('Assignment failed: Some seats have already been taken.');
          } else {
            alert('Assignment failed: Unexpected server response.');
          }
        } catch (e) {
          alert('Assignment failed: Network or server error.');
        }
      } else {
        console.log('Not enough valid guests entered to assign seats.');
      }
    }, [seatToGuestMap, minGuestCodes, setSeatToGuestMap, setGuestCodes, extensionMode, tableChangeRequired, groupTableId, tableCapacity, setTableChangeRequired, t, seatGuidToTableGuid, groupContext]);

  // Utility to join base URL and asset path safely
  function assetUrl(path: string) {
    const base = import.meta.env.BASE_URL;
    return base.endsWith('/') ? `${base}${path.replace(/^\//, '')}` : `${base}/${path.replace(/^\//, '')}`;
  }
  return (
    <div className="main-layout">
      <div className="caption-bar">
        <div className="caption-bar-logo">
          {logo && <img src={logo} alt="Logo" />}
          <div className="apptitle-box"><span className="app-title">{t("appTitle")}</span><br/><span className="app-subtitle">{t("appSubTitle")}</span></div>
        </div>
        <div className="caption-bar-actions">
          {!wsConnected && (
            <span className="ws-status-indicator" role="status" aria-live="polite" title={t('connectionLost')}>
              ? <span style={{color: 'red'}}>{t('connectionLost')}</span>
            </span>
          )}
          {/* Language selector and Logout button */}
          {/*<LanguageSwitcher assetUrl={assetUrl} />*/}
          <button
            className="logout-button"
            onClick={() => {
              sessionStorage.clear();
              window.location.reload();
            }}
            translate="no"
          >
            {t('logout')}
          </button>
        </div>
      </div>
      <div className="main-content">
        {children ? (
          children
        ) : (
          <>
            <div className="seatmap-container">
              <SeatMap
                roomData={roomData}
                seatToGuestMap={seatToGuestMap}
                guestCodes={guestCodes}
                onSeatClick={handleSeatClick}
                onTableClick={handleTableClick}
              />
            </div>
            <div className="guest-list-area" style={{ flex: 1, minWidth: 400, maxWidth: 700 }}>
              <GuestList
                seatToGuestMap={seatToGuestMap}
                minGuestCodes={minGuestCodes}
                maxGuestCodes={maxGuestCodes}
                guestCodes={guestCodes}
                onGuestCodesUpdate={handleGuestCodesUpdate}
                onAssignSeat={handleConfirmAssignments}
                onAssignmentsConfirmed={handleAssignmentsConfirmed}
                extensionMode={extensionMode}
                maxAdditionalGuests={effectiveMaxAdditional}
                tableChangeRequired={tableChangeRequired}
                noTablesAvailable={noTablesAvailable}
              />
            </div>
          </>
        )}
      </div>
      <div className="footer">
        <table><tbody><tr>
          <td>
        <img src={footerLogo} alt="Logo" className="footer-logo" />
        </td>
        <td>
        &copy; 2026 TablePlanner.com
        </td>
        </tr></tbody>
        </table>
      </div>
    </div>
  );
};

export default MainLayout;

